import { Button, Stack, Typography } from '@mui/material';
import { Dialog } from 'web-components-ts';
import { useTranslation } from 'react-i18next';
import { deleteBatchRecord } from 'api/batchApi';
import { useSnackbar } from 'notistack';
import { createMessage } from 'components/elements/snackbar/SnackBar';
import { ERROR, SUCCESS } from 'attrs/status';
import { useHistory } from 'react-router-dom';

interface DeleteBatchDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  batchId: string;
}

export default function DeleteBatchDialog({ open, setOpen, batchId }: DeleteBatchDialogProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const handleClose = () => {
    setOpen(false);
  };

  const onClickCloseDeleteBatch = () => {
    handleClose();
  };

  const onClickCancelDeleteBatch = () => {
    handleClose();
  };

  const onClickOKDeleteBatch = () => {
    const deleteBatch = async () => {
      try {
        await deleteBatchRecord(batchId);
        setTimeout(() => history.push('/batch-records'), 0);
        handleClose();
        enqueueSnackbar(createMessage('batch_records.delete_dialog.messages.success', SUCCESS, t), {
          variant: 'default',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
        });
      } catch {
        enqueueSnackbar(createMessage('batch_records.delete_dialog.messages.error', ERROR, t), {
          variant: 'default',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
        });
      }
    };
    deleteBatch();
  };

  const dialogContent = (
    <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>{t('batch_records.delete_dialog.message')}</Typography>
  );

  const dialogActionBar = (
    <Stack direction="row" spacing={2}>
      <Button variant="outlined" onClick={onClickCancelDeleteBatch}>
        {t('batch_records.delete_dialog.cancel_button')}
      </Button>
      <Button variant="contained" color="error" onClick={onClickOKDeleteBatch}>
        {t('default_actions.yes_delete')}
      </Button>
    </Stack>
  );

  return open ? (
    <Dialog
      open={open}
      handleClose={onClickCloseDeleteBatch}
      title={t('batch_records.delete_dialog.title')}
      subTitle={t('batch_records.delete_dialog.subtitle')}
      maxHeight="98vh"
      maxWidth="800px"
      canSave
      content={dialogContent}
      actionBar={dialogActionBar}
      justifyContentActionBar="flex-end"
    />
  ) : null;
}
